import type {
  CloudDiagnosticEnvironment,
  ComputerDiagnosticRoomRoute,
  ComputerDiagnosticTarget,
  ComputerExecutionDiagnostics,
  ComputerRuntimeLogRecall,
  ComputerRuntimeLogRecallDownload,
  ListComputerRuntimeLogRecallsReply,
  ListTuttiComputersReply,
  TuttiComputer,
} from '../types/tuttiComputer';
import {
  normalizeComputerConnectionStatus,
  normalizeRuntimeLogRangePreset,
  normalizeRuntimeLogRecallState,
  normalizeRuntimeLogUploadMode,
} from './tuttiComputerContract.ts';

type JsonObject = Record<string, unknown>;

function objectValue(value: unknown): JsonObject {
  return typeof value === 'object' && value !== null
    ? (value as JsonObject)
    : {};
}

function field(root: JsonObject, camelName: string, snakeName: string) {
  return root[camelName] ?? root[snakeName];
}

function stringField(root: JsonObject, camelName: string, snakeName: string) {
  const value = field(root, camelName, snakeName);
  return value === undefined || value === null ? '' : String(value);
}

function booleanField(root: JsonObject, camelName: string, snakeName: string) {
  return field(root, camelName, snakeName) === true;
}

export function parseTuttiComputer(value: unknown): TuttiComputer {
  const root = objectValue(value);
  const owner = objectValue(root.owner);
  return {
    computerId: stringField(root, 'computerId', 'computer_id'),
    displayName: stringField(root, 'displayName', 'display_name'),
    reportedName: stringField(root, 'reportedName', 'reported_name'),
    owner: {
      userId: stringField(owner, 'userId', 'user_id'),
      name: stringField(owner, 'name', 'name'),
      email: stringField(owner, 'email', 'email'),
    },
    connectionStatus: normalizeComputerConnectionStatus(
      field(root, 'connectionStatus', 'connection_status'),
    ),
    platform: stringField(root, 'platform', 'platform'),
    arch: stringField(root, 'arch', 'arch'),
    computerVersion: stringField(root, 'computerVersion', 'computer_version'),
    computerKind: stringField(root, 'computerKind', 'computer_kind'),
    canCollect: booleanField(root, 'canCollect', 'can_collect'),
    collectionBlockedReason: stringField(
      root,
      'collectionBlockedReason',
      'collection_blocked_reason',
    ),
    cloudEnvironment: parseCloudDiagnosticEnvironment(
      field(root, 'cloudEnvironment', 'cloud_environment'),
    ),
    bindingRevision: stringField(root, 'bindingRevision', 'binding_revision'),
    boundAt: stringField(root, 'boundAt', 'bound_at'),
    lastSeenAt: stringField(root, 'lastSeenAt', 'last_seen_at'),
  };
}

export function parseComputerRuntimeLogRecall(
  value: unknown,
): ComputerRuntimeLogRecall {
  const root = objectValue(value);
  return {
    taskId: stringField(root, 'taskId', 'task_id'),
    collectionSource: stringField(
      root,
      'collectionSource',
      'collection_source',
    ),
    computerId: stringField(root, 'computerId', 'computer_id'),
    boundUserId: stringField(root, 'boundUserId', 'bound_user_id'),
    bindingRevision: stringField(root, 'bindingRevision', 'binding_revision'),
    connectionId: stringField(root, 'connectionId', 'connection_id'),
    computerVersion: stringField(root, 'computerVersion', 'computer_version'),
    requestedByUserId: stringField(
      root,
      'requestedByUserId',
      'requested_by_user_id',
    ),
    rangePreset: normalizeRuntimeLogRangePreset(
      field(root, 'rangePreset', 'range_preset'),
    ),
    requestedStartAt: stringField(
      root,
      'requestedStartAt',
      'requested_start_at',
    ),
    requestedEndAt: stringField(root, 'requestedEndAt', 'requested_end_at'),
    state: normalizeRuntimeLogRecallState(field(root, 'state', 'state')),
    uploadMode: normalizeRuntimeLogUploadMode(
      field(root, 'uploadMode', 'upload_mode'),
    ),
    originalBytes: stringField(root, 'originalBytes', 'original_bytes'),
    compressedBytes: stringField(root, 'compressedBytes', 'compressed_bytes'),
    sha256: stringField(root, 'sha256', 'sha256'),
    actualStartAt: stringField(root, 'actualStartAt', 'actual_start_at'),
    actualEndAt: stringField(root, 'actualEndAt', 'actual_end_at'),
    unknownTimestampRows: stringField(
      root,
      'unknownTimestampRows',
      'unknown_timestamp_rows',
    ),
    truncated: booleanField(root, 'truncated', 'truncated'),
    collectionDurationMs: stringField(
      root,
      'collectionDurationMs',
      'collection_duration_ms',
    ),
    uploadDurationMs: stringField(
      root,
      'uploadDurationMs',
      'upload_duration_ms',
    ),
    failureCode: stringField(root, 'failureCode', 'failure_code'),
    failureMessage: stringField(root, 'failureMessage', 'failure_message'),
    acceptedAt: stringField(root, 'acceptedAt', 'accepted_at'),
    uploadStartedAt: stringField(root, 'uploadStartedAt', 'upload_started_at'),
    completedAt: stringField(root, 'completedAt', 'completed_at'),
    failedAt: stringField(root, 'failedAt', 'failed_at'),
    deadlineAt: stringField(root, 'deadlineAt', 'deadline_at'),
    downloadExpiresAt: stringField(
      root,
      'downloadExpiresAt',
      'download_expires_at',
    ),
    createdAt: stringField(root, 'createdAt', 'created_at'),
    updatedAt: stringField(root, 'updatedAt', 'updated_at'),
    downloadExpired: booleanField(root, 'downloadExpired', 'download_expired'),
  };
}

export function parseTuttiComputerList(
  value: unknown,
): ListTuttiComputersReply {
  const root = objectValue(value);
  const computers = field(root, 'computers', 'computers');
  return {
    computers: Array.isArray(computers)
      ? computers.map(parseTuttiComputer)
      : [],
    nextPageToken: stringField(root, 'nextPageToken', 'next_page_token'),
  };
}

export function parseComputerRuntimeLogRecallList(
  value: unknown,
): ListComputerRuntimeLogRecallsReply {
  const root = objectValue(value);
  const tasks = field(root, 'tasks', 'tasks');
  return {
    tasks: Array.isArray(tasks) ? tasks.map(parseComputerRuntimeLogRecall) : [],
    nextPageToken: stringField(root, 'nextPageToken', 'next_page_token'),
  };
}

export function parseComputerRuntimeLogRecallDownload(
  value: unknown,
): ComputerRuntimeLogRecallDownload {
  const root = objectValue(value);
  return {
    downloadUrl: stringField(root, 'downloadUrl', 'download_url'),
    expiresAt: stringField(root, 'expiresAt', 'expires_at'),
    fileName: stringField(root, 'fileName', 'file_name'),
  };
}

function parseCloudDiagnosticEnvironment(
  value: unknown,
): CloudDiagnosticEnvironment | null {
  if (value === undefined || value === null) return null;
  const root = objectValue(value);
  const providers = root.providers;
  return {
    environmentId: stringField(root, 'environmentId', 'environment_id'),
    state: stringField(root, 'state', 'state'),
    programReleaseId: stringField(
      root,
      'programReleaseId',
      'program_release_id',
    ),
    workerGeneration: stringField(
      root,
      'workerGeneration',
      'worker_generation',
    ),
    providers: Array.isArray(providers)
      ? providers.map((value) => {
          const provider = objectValue(value);
          return {
            providerType: stringField(
              provider,
              'providerType',
              'provider_type',
            ),
            state: stringField(provider, 'state', 'state'),
            detail: stringField(provider, 'detail', 'detail'),
          };
        })
      : [],
  };
}

export function parseComputerDiagnosticTarget(
  value: unknown,
): ComputerDiagnosticTarget {
  const root = objectValue(value);
  if (!root.computer) throw new Error('诊断目标响应缺少 Computer');
  return {
    computer: parseTuttiComputer(root.computer),
    targetId: stringField(root, 'targetId', 'target_id'),
    providerType: stringField(root, 'providerType', 'provider_type'),
  };
}

function parseComputerDiagnosticRoomRoute(
  value: unknown,
): ComputerDiagnosticRoomRoute | null {
  if (value === undefined || value === null) return null;
  const root = objectValue(value);
  const state = root.state;
  const identity = {
    roomId: stringField(root, 'roomId', 'room_id'),
    reason: stringField(root, 'reason', 'reason'),
  };
  if (state === 'missing') return { ...identity, state };
  if (state !== 'observed') throw new Error('Room route 响应缺少有效观测状态');
  return {
    ...identity,
    state,
    registered: booleanField(root, 'registered', 'registered'),
    dataSocketPresent: booleanField(
      root,
      'dataSocketPresent',
      'data_socket_present',
    ),
    sandboxTmpRegistered: booleanField(
      root,
      'sandboxTmpRegistered',
      'sandbox_tmp_registered',
    ),
    sandboxHomeRegistered: booleanField(
      root,
      'sandboxHomeRegistered',
      'sandbox_home_registered',
    ),
    homeviewRegistered: booleanField(
      root,
      'homeviewRegistered',
      'homeview_registered',
    ),
  };
}

export function parseComputerExecutionDiagnostics(
  value: unknown,
): ComputerExecutionDiagnostics {
  const root = objectValue(value);
  return {
    sessionId: stringField(root, 'sessionId', 'session_id'),
    turnId: stringField(root, 'turnId', 'turn_id'),
    state: stringField(root, 'state', 'state'),
    commandState: stringField(root, 'commandState', 'command_state'),
    roomRoute: parseComputerDiagnosticRoomRoute(
      field(root, 'roomRoute', 'room_route'),
    ),
    phases: Array.isArray(root.phases)
      ? root.phases.map((value) => {
          const phase = objectValue(value);
          return {
            phase: stringField(phase, 'phase', 'phase'),
            recorded: booleanField(phase, 'recorded', 'recorded'),
            durationMs: stringField(phase, 'durationMs', 'duration_ms'),
            startedAt: stringField(phase, 'startedAt', 'started_at'),
            completedAt: stringField(phase, 'completedAt', 'completed_at'),
            detail: stringField(phase, 'detail', 'detail'),
          };
        })
      : [],
  };
}
